import type { AuthSnapshot } from "../auth-refresh-coordinator"

import { BACKEND_URI } from "@/constants/config"
import { logger } from "@/lib/logger"

import {
  AuthMode,
  getAuthMode,
  handleAuthRedirect,
  isCorsRedirectError,
  setupTokenRefreshScheduler,
  useLogin,
} from "../auth-api"
import {
  captureAuthSnapshot,
  getManagedCredential,
  isAuthCredentialCurrent,
  isAuthSnapshotCurrent,
  recoverFromUnauthorized,
  requestTerminalRecovery,
} from "../auth-refresh-coordinator"

// Initialize token refresh scheduler at module-load time. Importing this
// shared module from any per-domain client file ensures the scheduler is
// armed exactly once per page load.
setupTokenRefreshScheduler()

export { BACKEND_URI }

/**
 * Thrown by `handleApiError` for any non-401 error response. Extends `Error`
 * so every existing `catch (error) { error instanceof Error ... }` /
 * `error.message` call site keeps working byte-for-byte (message format is
 * unchanged) — this only ADDS `status` and `code`/`bodyError` so a caller
 * that needs to branch on the exact HTTP status or the backend's structured
 * `{ code: "..." }` (or plain `{ error: "..." }`) body can do so without
 * parsing `message` text. See durable-turn transport fallback (issue #2513)
 * for the first consumer.
 */
export class ApiError extends Error {
  readonly status: number
  /** Backend's structured `errorData.code`, when present (e.g. `"durable_turns_disabled"`). */
  readonly code?: string
  /** Backend's raw `errorData.error` string, when present — used when a
   *  caller must distinguish response shapes that have no `code` field
   *  (e.g. the SPA catch-all's `"Unknown API route: ..."` vs a route's own
   *  `"Session not found"`, both plain 404s with no `code`). */
  readonly bodyError?: string
  /**
   * The parsed JSON error body, verbatim, when the response had one.
   *
   * `code`/`bodyError` above lift the two scalar fields every backend error
   * envelope carries; this keeps whatever else rode along, for the callers
   * that need a structured payload rather than a string. First consumer: the
   * fair-use gate's `429 {error: "fair_use_blocked", usage: {...}}` on the
   * durable accept endpoint — the usage snapshot in it is what refreshes the
   * composer indicator and opens the request-more flow (#2625). Typed
   * `unknown`: it is untrusted wire data, so every reader must narrow it
   * (`extractFairUseError` does).
   */
  readonly body?: unknown

  constructor(
    message: string,
    status: number,
    options: { code?: string; bodyError?: string; body?: unknown } = {}
  ) {
    super(message)
    this.status = status
    this.code = options.code
    this.bodyError = options.bodyError
    this.body = options.body
  }
}

export type WhatsNewPayload = {
  version: string
  // Legacy JSON structure
  items?: string[]
  // New Markdown structure
  markdown?: string
  // Server-side decision on whether to show the dialog
  shouldShow?: boolean
  // Set when serving an admin preview
  preview?: boolean
  // Locale the client asked for (after the language-tag/region split applied
  // by the backend). Useful for admin preview to compare against resolvedLocale.
  requestedLocale?: string
  // Locale the backend actually served — may differ from requestedLocale
  // when only DEFAULT_LOCALE has a file for the resolved version.
  resolvedLocale?: string
}

/**
 * The legacy `AUTH_REFRESHED` sentinel, now carrying the 401 response that
 * produced it. `message` is unchanged, so every existing
 * `error.message === "AUTH_REFRESHED"` check still matches byte-for-byte; the
 * attached response only lets `fetchWithAuthRetry` build a correctly-shaped
 * `ApiError` when it turns out the request cannot be replayed after all.
 */
class AuthRefreshedSignal extends Error {
  readonly response: Response

  constructor(response: Response) {
    super("AUTH_REFRESHED")
    this.response = response
  }
}

/** Auth state captured when the request was dispatched, plus its abort signal. */
export interface AuthRetryContext {
  snapshot: AuthSnapshot
  /** Credential actually sent by the bounded replay, within the original generation. */
  replaySnapshot?: AuthSnapshot
  signal?: AbortSignal | null
}

function abortErrorFor(signal: AbortSignal | null | undefined): unknown {
  if (signal?.reason !== undefined) return signal.reason
  return new DOMException("The operation was aborted.", "AbortError")
}

function unauthorizedApiError(response: Response): ApiError {
  return new ApiError(`HTTP error! status: 401\n${response.statusText}`, 401)
}

export async function handleApiError(
  response: Response,
  context?: AuthRetryContext
): Promise<never> {
  // Check if this is an authentication error (401)
  if (response.status === 401) {
    logger.warn(`Authentication error (401): ${response.statusText}`)

    // Recover through the single-flight coordinator, using the auth state
    // captured when this request was dispatched. Callers that can't snapshot at
    // dispatch fall back to snapshotting now — still correct, just less able to
    // recognise a 401 that a concurrent refresh has already made obsolete.
    const outcome = await recoverFromUnauthorized(context?.snapshot ?? captureAuthSnapshot(), {
      signal: context?.signal,
    })

    if (outcome.status === "refreshed" || outcome.status === "already-refreshed") {
      // Credentials changed — the caller may replay exactly once.
      throw new AuthRefreshedSignal(response)
    }

    if (outcome.status === "cancelled") {
      // The caller went away while we waited. It must not replay and must not
      // drag the session into a forced logout on its way out.
      throw abortErrorFor(context?.signal)
    }

    if (outcome.status === "session-changed") {
      // A different session was established while this request was in flight.
      // Replaying would run user A's request as user B, and forcing a logout
      // would destroy a session that is perfectly alive — so surface the 401 as
      // an ordinary ApiError instead.
      logger.warn("Dropping a 401 that crossed an auth session change")
      throw unauthorizedApiError(response)
    }

    // Unrecoverable: forced re-login (deduplicated inside the coordinator).
    throw new Error(`AUTHENTICATION_ERROR: ${response.statusText}`)
  }

  // Handle other errors
  try {
    const errorData = await response.json()
    logger.error("API error response", {
      status: response.status,
      statusText: response.statusText,
      errorData,
    })
    // Keep the thrown message byte-for-byte identical to the legacy plain
    // `Error` this class replaces (`HTTP error! status: ${status}\n${statusText}`,
    // no `errorData.error` line) — the richer detail from the response body
    // is carried in the structured `code`/`bodyError` fields below instead of
    // being spliced into `message`, so no `.message`/`.toString()` render
    // site anywhere in the app changes for this change.
    throw new ApiError(
      `HTTP error! status: ${response.status}\n${response.statusText}`,
      response.status,
      {
        code: typeof errorData?.code === "string" ? errorData.code : undefined,
        bodyError: typeof errorData?.error === "string" ? errorData.error : undefined,
        body: errorData,
      }
    )
  } catch (error) {
    // If the error message is about auth refresh, rethrow it
    if (
      error instanceof Error &&
      (error.message === "AUTH_REFRESHED" || error.message.startsWith("AUTHENTICATION_ERROR"))
    ) {
      throw error
    }
    // The ApiError thrown above must pass through unchanged — the outer
    // try/catch here only exists to also catch `response.json()` parse
    // failures below, not to re-wrap a well-formed ApiError.
    if (error instanceof ApiError) {
      throw error
    }

    logger.error("Error parsing error response", error as Error)
    throw new ApiError(
      `HTTP error! status: ${response.status}\n${response.statusText}`,
      response.status
    )
  }
}

export function getHeaders(
  idToken: string | undefined,
  isUpload: boolean = false
): Record<string, string> {
  const headers: Record<string, string> = {}

  if (!isUpload) {
    headers["Content-Type"] = "application/json"
  }

  const authMode = getAuthMode()

  if (useLogin && authMode === AuthMode.MSAL) {
    if (idToken) {
      headers["Authorization"] = `Bearer ${idToken}`
    }
  }
  return headers
}

// Classifies a fetch failure and either recovers (forced re-login navigation)
// or rethrows a clear error. Shared by the first attempt and the post-refresh
// retry below — a retry that hits a still-dead session (e.g. redirected
// cross-origin to the identity provider) must get the same CORS-redirect /
// forced-logout handling as the first attempt, not escape as a raw, unclassified
// error that never triggers a re-login (see #2597).
async function classifyFetchFailure(
  error: unknown,
  url: string,
  context: AuthRetryContext
): Promise<never> {
  if (context.signal?.aborted) throw abortErrorFor(context.signal)
  if (!isAuthSnapshotCurrent(context.snapshot)) throw error
  const rejectedSnapshot = context.replaySnapshot ?? context.snapshot
  if (error instanceof Error && error.message.startsWith("AUTHENTICATION_ERROR")) {
    // This is an authentication error that couldn't be resolved by refreshing
    logger.error("Authentication error that couldn't be resolved", { error })

    // Force re-login if not already doing so. Routed through the coordinator so
    // this and the recovery path can't each fire their own navigation for the
    // same incident (which is how three concurrent 401s used to produce six
    // logouts and trip `guardAuthLoop`'s burst threshold).
    if (!url.includes("/logout")) {
      requestTerminalRecovery(rejectedSnapshot, { signal: context.signal })
    }

    // Rethrow with a clear message
    throw new Error(`Authentication failed: ${error.message.replace("AUTHENTICATION_ERROR: ", "")}`)
  } else if (isCorsRedirectError(error)) {
    // This is likely a CORS error from a 302 redirect
    // Handle with a full page navigation instead of AJAX
    const authMode = getAuthMode()
    if (authMode === AuthMode.EASYAUTH) {
      if (!isAuthCredentialCurrent(rejectedSnapshot)) throw error
      requestTerminalRecovery(rejectedSnapshot, { signal: context.signal }, handleAuthRedirect)

      // Return a never-resolving promise since we're navigating away
      return new Promise<never>(() => {
        // This promise intentionally never resolves
      })
    }
    // MSAL mode: API auth travels in headers (401 JSON on failure), never a
    // 302 redirect — so an opaque "Failed to fetch" here means the backend
    // is unreachable (restart/deploy/network), NOT an auth problem. Forcing
    // logout reloaded the whole app mid-session on every backend blip,
    // discarding in-progress chat state. Surface a network error instead.
    logger.error("Network error reaching backend (not treating as auth redirect in MSAL mode)", {
      authMode,
      error: error instanceof Error ? error.message : String(error),
    })
    throw new Error("The server could not be reached. Please try again.")
  } else {
    // Rethrow other errors
    if (error instanceof Error) {
      logger.error("Error in API request", error)
    } else {
      logger.error("Unknown error in API request")
    }
    throw error
  }
}

/**
 * A body that has already been consumed (or can only be read once) cannot be
 * re-sent. Everything else fetch accepts — strings, FormData, Blob,
 * ArrayBuffer, URLSearchParams — is safely replayable.
 */
function isReplayableBody(body: BodyInit | null | undefined): boolean {
  if (body === null || body === undefined) return true
  return !(typeof ReadableStream !== "undefined" && body instanceof ReadableStream)
}

/**
 * Builds the replay request. The caller's `options` object is never mutated and
 * its headers are copied verbatim — `Content-Type`, custom headers and an
 * independently-supplied `Authorization` all survive untouched.
 *
 * The only header this rewrites is the bearer we manage ourselves, and only
 * when the request was actually carrying it (or carrying none at all): a call
 * site that brought its own credentials keeps them. The replacement is read
 * from the coordinator at replay time, NOT from the token the caller captured
 * before dispatch — that stale token is exactly what produced the 401.
 */
function buildReplayOptions(
  options: RequestInit,
  snapshot: AuthSnapshot,
  idToken: string | undefined
): RequestInit {
  const headers = new Headers(options.headers ?? undefined)

  if (useLogin && getAuthMode() === AuthMode.MSAL) {
    const managed = getManagedCredential()
    const existing = headers.get("Authorization")
    // Ours if the request carried no bearer at all, the bearer we were managing
    // at dispatch, or the token this call site was handed (which is how
    // `getHeaders(idToken)` built the header on the first attempt).
    const carriedOurBearer =
      existing === null ||
      (snapshot.credential !== undefined && existing === `Bearer ${snapshot.credential}`) ||
      (idToken !== undefined && existing === `Bearer ${idToken}`)

    if (managed && carriedOurBearer) {
      headers.set("Authorization", `Bearer ${managed}`)
    }
  }

  return { ...options, headers }
}

/**
 * Sends the one permitted replay. A 401 here is terminal: the *fresh*
 * credential was rejected too, so refreshing again could only loop.
 */
async function replayOnce(
  url: string,
  options: RequestInit,
  context: AuthRetryContext
): Promise<Response> {
  if (context.signal?.aborted) throw abortErrorFor(context.signal)
  if (!isAuthSnapshotCurrent(context.snapshot)) {
    throw new ApiError("HTTP error! status: 401\nUnauthorized", 401)
  }
  context.replaySnapshot = { ...captureAuthSnapshot(), generation: context.snapshot.generation }
  const retryResponse = await fetch(url, options)

  if (retryResponse.status === 401) {
    throw new Error(`AUTHENTICATION_ERROR: ${retryResponse.statusText}`)
  }
  if (!retryResponse.ok) {
    await handleApiError(retryResponse, context)
  }

  return retryResponse
}

export async function fetchWithAuthRetry(
  url: string,
  options: RequestInit,
  idToken?: string
): Promise<Response> {
  const signal = options.signal ?? null
  // Capture auth state at dispatch so a 401 that arrives after some other
  // request already refreshed the credential is recognised as stale and simply
  // replayed, instead of triggering a second refresh.
  const context: AuthRetryContext = { snapshot: captureAuthSnapshot(), signal }

  try {
    // Bind a supplied app bearer to the same current credential/epoch captured
    // above. Preserve header-less first attempts and independent credentials.
    const initialOptions = new Headers(options.headers).has("Authorization")
      ? buildReplayOptions(options, context.snapshot, idToken)
      : options
    if (!isAuthSnapshotCurrent(context.snapshot)) {
      throw new ApiError("HTTP error! status: 401\nUnauthorized", 401)
    }
    const response = await fetch(url, initialOptions)

    if (!response.ok) {
      await handleApiError(response, context)
    }

    return response
  } catch (error) {
    // Check if this is an AUTH_REFRESHED error, which means we should retry the request
    if (error instanceof Error && error.message === "AUTH_REFRESHED") {
      // Cancel point: don't replay a request whose caller has already left.
      if (signal?.aborted) throw abortErrorFor(signal)

      if (!isReplayableBody(options.body)) {
        logger.warn("Skipping post-refresh replay: request body cannot be re-sent")
        throw error instanceof AuthRefreshedSignal
          ? unauthorizedApiError(error.response)
          : new ApiError("HTTP error! status: 401\nUnauthorized", 401)
      }

      logger.info("Authentication refreshed, retrying request")

      try {
        return await replayOnce(
          url,
          buildReplayOptions(options, context.snapshot, idToken),
          context
        )
      } catch (retryError) {
        return await classifyFetchFailure(retryError, url, context)
      }
    }

    return await classifyFetchFailure(error, url, context)
  }
}

/**
 * MUTATION: Logs out the current user and redirects to logout page
 * @category Mutation
 */
export async function logoutApi(): Promise<void> {
  try {
    await fetchWithAuthRetry(`${BACKEND_URI}/logout`, {
      method: "POST",
    })
  } catch (error) {
    logger.warn("Error during API logout, continuing with redirect", error as Error)
  }

  // Redirect to EasyAuth logout without a return URL.
  // User will stay on Microsoft's signed-out page and can navigate back to sign in again.
  // This avoids CORS issues from stale cached JS trying to load during the redirect.
  window.location.href = `${window.location.origin}/.auth/logout`
}
